import React, { useEffect, useState } from "react";
import { PageHeader } from "@/pages/About";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { contentApi } from "@/lib/api";
import LevelBadge from "@/components/LevelBadge";
import { Loader2, ClipboardList, FolderCheck, ListChecks, ShieldAlert, UserCheck } from "lucide-react";

const AssessmentCriteria = () => {
  const [fw, setFw] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    contentApi.framework().then(({ data }) => setFw(data)).finally(() => setLoading(false));
  }, []);

  const s = fw?.scoring || {};
  const minOverall = s.min_overall_score ?? 70;
  const minCat = s.min_category_score ?? 60;
  const strong = s.strong_evidence_levels || ["Records available", "Implemented", "Document reviewed"];
  const caps = s.evidence_score_cap || {};
  const defs = fw?.evidence_level_definitions || {};
  const reqDocs = s.required_docs_total ?? 47;

  const StepCard = ({ icon: Icon, n, title, children }) => (
    <Card data-testid={`scoring-step-${n}`}>
      <CardContent className="p-6">
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-wgreen-100"><Icon className="h-5 w-5 text-wgreen-700" /></div>
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-navy-900 text-xs font-semibold text-white">{n}</span>
          <h3 className="font-heading text-sm font-semibold text-navy-900">{title}</h3>
        </div>
        <div className="space-y-2 text-sm leading-relaxed text-[var(--wechr-muted-text)]">{children}</div>
      </CardContent>
    </Card>
  );

  return (
    <div>
      <PageHeader eyebrow="Assessment Criteria" title="How WECH-RB scores your organization"
        subtitle="WECH-RB certification is evidence-based, not a self-assessment. Here's exactly how your questionnaire answers and uploaded documents translate into a score — and why a high number alone does not guarantee certification." />

      <section className="wechr-section">
        <div className="wechr-container">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-wgreen-600" /></div>
          ) : fw && (
            <div className="space-y-14">

              {/* ---- Scoring methodology ---- */}
              <div>
                <h2 className="font-heading text-xl font-semibold text-navy-900">How scoring works</h2>
                <p className="mb-6 mt-1 max-w-3xl text-sm text-[var(--wechr-muted-text)]">
                  Each assessment category is scored from 0–100 by a reviewer (assisted by an AI gap analysis) based
                  on the evidence you provide. Your overall score is the average of your in-scope categories, each weighted
                  equally — for full certification that is one-sixth (about 16.7%) per category; for category-based
                  certification the weighting is split equally across the categories you select.
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  <StepCard icon={ClipboardList} n={1} title="Questionnaire answers give context — not proof">
                    <p>Your questionnaire responses describe what your organization does and point reviewers to the right documents.
                    They are <strong className="text-navy-800">self-declarations</strong>, so on their own they do <strong className="text-navy-800">not</strong> raise your score.
                    A "yes" answer with no supporting document is treated as unproven.</p>
                  </StepCard>

                  <StepCard icon={FolderCheck} n={2} title="Uploaded evidence drives the score">
                    <p>Points are awarded for the <strong className="text-navy-800">actual evidence</strong> in your uploaded documents. The stronger and more
                    operational the evidence, the higher a category can score. Each evidence level sets a hard ceiling on the score:</p>
                    <div className="mt-2 overflow-hidden rounded-lg border border-[var(--wechr-border)]">
                      <table className="w-full text-xs">
                        <thead className="bg-[var(--wechr-subtle)] text-navy-900">
                          <tr><th className="px-3 py-2 text-left font-semibold">Evidence level</th><th className="px-3 py-2 text-right font-semibold">Max score</th></tr>
                        </thead>
                        <tbody>
                          {(fw.evidence_levels || []).map((lvl) => (
                            <tr key={lvl} className="border-t border-[var(--wechr-border)]">
                              <td className="px-3 py-1.5 text-navy-800">{lvl}</td>
                              <td className="px-3 py-1.5 text-right font-semibold text-navy-900">{caps[lvl] ?? "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs">So a category supported only by a <strong className="text-navy-800">policy</strong> is capped at {caps["Policy only"] ?? 45}/100, no matter how complete the questionnaire is.</p>
                  </StepCard>

                  <StepCard icon={ListChecks} n={3} title="Minimum evidence required">
                    <p>To be certifiable, <strong className="text-navy-800">every</strong> category must reach at least:</p>
                    <ul className="ml-1 list-inside list-disc space-y-1">
                      <li>a category score of <strong className="text-navy-800">{minCat}</strong> or higher, and</li>
                      <li>evidence of at least <strong className="text-navy-800">“{strong[0]}”</strong> level (records/implementation — not policies or procedures alone).</li>
                    </ul>
                    <p>All <strong className="text-navy-800">{reqDocs}</strong> document topics are required — each requirement needs its own uploaded evidence.</p>
                  </StepCard>

                  <StepCard icon={ShieldAlert} n={4} title="Why certification can be blocked despite a high score">
                    <p>A strong overall number is <strong className="text-navy-800">not</strong> enough on its own. Certification is blocked if any of these fail — even when the average looks high:</p>
                    <ul className="ml-1 list-inside list-disc space-y-1">
                      <li>overall score below <strong className="text-navy-800">{minOverall}</strong>;</li>
                      <li>any single category below <strong className="text-navy-800">{minCat}</strong>;</li>
                      <li>any category lacking records-level evidence;</li>
                      <li>any of the {reqDocs} required documents missing.</li>
                    </ul>
                    <p>This prevents a weak area from being "averaged out" by stronger ones.</p>
                  </StepCard>
                </div>

                <div className="mt-4">
                  <StepCard icon={UserCheck} n={5} title="Final certification is decided by an Admin reviewer">
                    <p>The AI gap analysis is only a preliminary aid used internally. A WECH-RB reviewer verifies your documents,
                    can <strong className="text-navy-800">adjust the category scores and evidence levels</strong>, and makes the final decision. A certificate is
                    issued <strong className="text-navy-800">only when a reviewer approves</strong> and all of the eligibility rules above are met. Reviewers may also request
                    additional information before deciding. In short: <strong className="text-navy-800">score + sufficient evidence in every area + all documents + human approval</strong> — all four are required.</p>
                  </StepCard>
                </div>
              </div>

              {/* ---- Categories ---- */}
              <div>
                <h2 className="font-heading text-xl font-semibold text-navy-900">Assessment Categories</h2>
                <p className="mb-4 mt-1 text-sm text-[var(--wechr-muted-text)]">Six categories — for full certification each contributes equally, about 16.7% (one-sixth) of the overall score. Category-based scores are averaged equally across the categories you select.</p>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {fw.categories.map((c) => (
                    <Card key={c.key}>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <h3 className="font-heading text-sm font-semibold text-navy-900">{c.name}</h3>
                          <span className="rounded-full bg-wgreen-100 px-2.5 py-1 text-xs font-semibold text-wgreen-700">1/{fw.categories.length} of score</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* ---- Levels ---- */}
              <div>
                <h2 className="font-heading text-xl font-semibold text-navy-900">Certification Levels</h2>
                <p className="mb-4 mt-1 text-sm text-[var(--wechr-muted-text)]">Awarded only after eligibility is met and a reviewer approves. Below {minOverall} is “Not Certified”.</p>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {(fw.levels || []).filter((l) => l.level !== "Not Certified").map((item) => (
                    <Card key={item.level}><CardContent className="flex flex-col items-center p-6">
                      <LevelBadge level={item.level} size={96} showLabel={false} />
                      <p className="mt-3 font-heading text-sm font-semibold text-navy-900">{item.level}</p>
                      <p className="text-sm text-[var(--wechr-muted-text)]">Score {item.min}–{item.max}</p>
                    </CardContent></Card>
                  ))}
                </div>
              </div>

              {/* ---- Evidence & Risk ---- */}
              <div>
                <h2 className="font-heading text-xl font-semibold text-navy-900">Evidence Levels explained</h2>
                <p className="mb-4 mt-1 max-w-3xl text-sm text-[var(--wechr-muted-text)]">
                  Each requirement is graded on the strength of the evidence you provide, from weakest to strongest. The level sets
                  the highest score that requirement can earn. Note that photos or visual proof of a practice count as real evidence
                  ("Records available") even if it isn't written up as a formal policy.
                </p>
                <div className="space-y-3">
                  {(fw.evidence_levels || []).map((e, i) => (
                    <Card key={e} data-testid={`evidence-def-${i}`}>
                      <CardContent className="flex flex-col gap-2 p-5 sm:flex-row sm:items-start sm:gap-4">
                        <div className="flex flex-shrink-0 items-center gap-3 sm:w-56">
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-navy-900 text-xs font-semibold text-white">{i + 1}</span>
                          <div>
                            <p className="font-heading text-sm font-semibold text-navy-900">{e}</p>
                            <p className="text-xs text-[var(--wechr-muted-text)]">Max {caps[e] ?? "—"}/100{strong.includes(e) ? " · qualifies for certification" : ""}</p>
                          </div>
                        </div>
                        <p className="text-sm leading-relaxed text-[var(--wechr-muted-text)]">{defs[e] || ""}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <div className="mt-6">
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Risk Ratings</CardTitle></CardHeader>
                    <CardContent><div className="flex flex-wrap gap-2">{fw.risk_ratings.map((r) => (
                      <span key={r} className="rounded-full border border-[var(--wechr-border)] px-3 py-1 text-sm text-navy-800">{r}</span>
                    ))}</div>
                    <p className="mt-4 text-sm text-[var(--wechr-muted-text)]">Risk ratings help reviewers prioritize follow-up and decide whether enhanced review is required before a decision.</p></CardContent>
                  </Card>
                </div>
              </div>

            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AssessmentCriteria;
